/**
 * CalculationBreakdown Component
 * Shows step-by-step SSP calculations for transparency and verification
 */

import React, { useState } from 'react';
import { Check, X, ChevronDown, ChevronUp, Calculator, AlertCircle, Info } from 'lucide-react';
import { formatCurrency } from '../utils/sspCalculations';
import {
  SSP_WEEKLY_RATE,
  SSP_WEEKLY_RATE_2026,
  LOWER_EARNINGS_LIMIT,
  WAITING_DAYS_CURRENT,
  EIGHTY_PERCENT_THRESHOLD_2026,
} from '../data/constants';

export default function CalculationBreakdown({
  aweResult,
  currentSSP,
  newSSP,
  employee,
  absence,
  show = false,
}) {
  const [isExpanded, setIsExpanded] = useState(show);

  if (!currentSSP && !newSSP) return null;

  return (
    <div className="mt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-slate-600" />
          <span className="font-semibold text-slate-900">Calculation Breakdown</span>
          <span className="text-xs text-slate-500">(Step-by-step explanation)</span>
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-600" /> : <ChevronDown className="w-5 h-5 text-slate-600" />}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-6 animate-fade-in">
          {/* AWE Calculation */}
          <div className="card">
            <div className="card-header bg-slate-50">
              <h3 className="font-semibold text-slate-900">Step 1: Average Weekly Earnings (AWE)</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-700 mb-2">
                    {aweResult.payFrequency === 'monthly'
                      ? 'Monthly-paid employee: Using the last pay periods received:'
                      : aweResult.weeksEmployed < 8
                      ? `New employee (${aweResult.weeksEmployed} weeks): Using actual earnings received:`
                      : 'Add up the gross earnings from the 8 weeks before sickness began:'
                    }
                  </p>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    {aweResult.earnings.map((earning, i) => (
                      <div key={i} className="bg-slate-50 p-2 rounded text-center">
                        <p className="text-slate-500">
                          {aweResult.payFrequency === 'monthly'
                            ? `Month ${i + 1}`
                            : `Week ${i + 1}`
                          }
                        </p>
                        <p className="font-mono font-medium">{formatCurrency(earning)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-700">
                    {aweResult.payFrequency === 'monthly'
                      ? `Total earnings (${aweResult.periodCount || 0} months):`
                      : `Total earnings (${aweResult.periodCount || 8} weeks):`
                    }
                  </span>
                  <span className="font-mono font-semibold">{formatCurrency(aweResult.total)}</span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-700">Average Weekly Earnings (AWE):</span>
                  <span className="font-mono font-bold text-lg">{formatCurrency(aweResult.awe)}</span>
                  {aweResult.method && (
                    <span className="text-slate-500 text-xs">({aweResult.method})</span>
                  )}
                </div>

                {/* Calculation Method Info */}
                {(aweResult.payFrequency === 'monthly' || aweResult.weeksEmployed < 8) && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-blue-800">
                        {aweResult.payFrequency === 'monthly' ? (
                          <p>
                            For monthly-paid employees, AWE is calculated as: (Total earnings ÷ months) × 12 ÷ 52.
                            This converts monthly pay to an equivalent weekly average.
                          </p>
                        ) : (
                          <p>
                            New employees with less than 8 weeks of earnings: AWE = Total earnings ÷ actual weeks employed.
                            This ensures fair SSP calculation based on their actual pay history.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* LEL Check */}
                <div className={`mt-3 p-3 rounded-lg border ${
                  aweResult.meetsLEL
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-2">
                    {aweResult.meetsLEL ? (
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <div className="text-sm">
                      <p className={`font-medium ${aweResult.meetsLEL ? 'text-green-900' : 'text-red-900'}`}>
                        {aweResult.meetsLEL ? '✓ Meets' : '✗ Below'} Lower Earnings Limit (Current Rules)
                      </p>
                      <p className={aweResult.meetsLEL ? 'text-green-700' : 'text-red-700'}>
                        AWE {formatCurrency(aweResult.awe)} is {aweResult.meetsLEL ? 'at or above' : 'below'} the LEL threshold of {formatCurrency(LOWER_EARNINGS_LIMIT)}/week
                        {!aweResult.meetsLEL && ` (shortfall: ${formatCurrency(aweResult.lelShortfall)})`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Side-by-Side Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current Rules */}
            <div className="card border-primary-200">
              <div className="card-header bg-primary-50">
                <h3 className="font-semibold text-primary-900">Current Rules (2025/26)</h3>
              </div>
              <div className="card-body space-y-4">
                {/* Step 2: Eligibility Check */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Step 2: Eligibility Checks</h4>
                  <div className="space-y-2 text-sm">
                    {/* LEL Check */}
                    <div className="flex items-start gap-2">
                      {aweResult.meetsLEL ? (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className={aweResult.meetsLEL ? 'text-green-700' : 'text-red-700'}>
                          <strong>Lower Earnings Limit:</strong> {formatCurrency(aweResult.awe)} {aweResult.meetsLEL ? '≥' : '<'} {formatCurrency(LOWER_EARNINGS_LIMIT)}
                        </p>
                      </div>
                    </div>

                    {/* PIW Check */}
                    <div className="flex items-start gap-2">
                      {absence.totalSickDays >= 4 ? (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className={absence.totalSickDays >= 4 ? 'text-green-700' : 'text-red-700'}>
                          <strong>Period of Incapacity (PIW):</strong> {absence.totalSickDays} days {absence.totalSickDays >= 4 ? '≥' : '<'} 4 days required
                        </p>
                        {absence.totalSickDays < 4 && (
                          <p className="text-xs text-red-600 mt-1">
                            Must be off for 4+ consecutive days to qualify for SSP
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Eligibility Result */}
                    {currentSSP.eligible ? (
                      <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                        <p className="text-green-900 font-medium">✓ Eligible for SSP</p>
                      </div>
                    ) : (
                      <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                        <p className="text-red-900 font-medium">✗ Not Eligible</p>
                        <p className="text-sm text-red-700 mt-1">{currentSSP.reason}</p>
                      </div>
                    )}
                  </div>
                </div>

                {currentSSP.eligible && (
                  <>
                    {/* Step 3: Daily Rate */}
                    <div className="pt-4 border-t border-slate-200">
                      <h4 className="font-medium text-slate-900 mb-2">Step 3: Daily SSP Rate</h4>
                      <div className="space-y-2 text-sm">
                        <p className="text-slate-700">
                          Weekly SSP rate: <span className="font-mono font-semibold">{formatCurrency(SSP_WEEKLY_RATE)}</span>
                        </p>
                        <p className="text-slate-700">
                          Qualifying days per week: <span className="font-semibold">{employee.qualifyingDays}</span>
                        </p>
                        <div className="bg-primary-50 p-2 rounded mt-2">
                          <p className="text-primary-900">
                            Daily rate = {formatCurrency(SSP_WEEKLY_RATE)} ÷ {employee.qualifyingDays} = <span className="font-mono font-bold">{formatCurrency(currentSSP.dailyRate)}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Step 4: Waiting Days */}
                    <div className="pt-4 border-t border-slate-200">
                      <h4 className="font-medium text-slate-900 mb-2">Step 4: Apply Waiting Days</h4>
                      <div className="space-y-2 text-sm">
                        <p className="text-slate-700">
                          Rostered qualifying days during absence: <span className="font-semibold">{currentSSP.rosteredDays || absence.rosteredDays}</span>
                        </p>
                        <p className="text-slate-700">
                          Waiting days (first 3 qualifying days): <span className="font-semibold text-red-600">-{currentSSP.waitingDays}</span>
                          {absence.isLinkedPeriod && <span className="text-green-600 ml-2">(Linked period - no waiting days)</span>}
                        </p>
                        <div className="bg-primary-50 p-2 rounded mt-2">
                          <p className="text-primary-900">
                            SSP payable days = {currentSSP.rosteredDays || absence.rosteredDays} - {currentSSP.waitingDays} = <span className="font-semibold">{currentSSP.sspDaysPaid}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Step 5: Total SSP */}
                    <div className="pt-4 border-t border-slate-200">
                      <h4 className="font-medium text-slate-900 mb-2">Step 5: Total SSP</h4>
                      <div className="bg-primary-100 p-3 rounded">
                        <p className="text-sm text-primary-700 mb-1">
                          {currentSSP.sspDaysPaid} days × {formatCurrency(currentSSP.dailyRate)}/day
                        </p>
                        <p className="text-2xl font-bold text-primary-900 font-mono">
                          {formatCurrency(currentSSP.totalSSP)}
                        </p>
                      </div>
                    </div>

                    {/* 28-Week Maximum Tracking */}
                    {(currentSSP.weeksUsed > 0 || currentSSP.wasCapped) && (
                      <div className="pt-4 border-t border-slate-200">
                        <h4 className="font-medium text-slate-900 mb-2">28-Week Maximum</h4>
                        <div className={`p-3 rounded border ${
                          currentSSP.maxReached
                            ? 'bg-red-50 border-red-300'
                            : currentSSP.weeksRemaining < 4
                            ? 'bg-amber-50 border-amber-300'
                            : 'bg-blue-50 border-blue-300'
                        }`}>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-700">Previous SSP weeks:</span>
                              <span className="font-mono font-semibold">{(absence.previousSSPWeeks || 0).toFixed(1)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-700">This absence adds:</span>
                              <span className="font-mono font-semibold">+{(currentSSP.weeksUsed - (absence.previousSSPWeeks || 0)).toFixed(1)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-slate-300">
                              <span className="font-medium text-slate-900">Total weeks used:</span>
                              <span className="font-mono font-bold text-lg">{currentSSP.weeksUsed.toFixed(1)} / 28</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-700">Weeks remaining:</span>
                              <span className={`font-mono font-semibold ${
                                currentSSP.weeksRemaining < 4 ? 'text-amber-700' : 'text-green-700'
                              }`}>
                                {currentSSP.weeksRemaining.toFixed(1)}
                              </span>
                            </div>
                            {currentSSP.wasCapped && (
                              <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded">
                                <p className="text-amber-900 font-medium flex items-center gap-1">
                                  <AlertCircle className="w-4 h-4" />
                                  Payment Capped
                                </p>
                                <p className="text-xs text-amber-800 mt-1">
                                  SSP limited to remaining entitlement
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!currentSSP.eligible && (
                  <div className="pt-4 border-t border-slate-200">
                    <div className="bg-red-100 p-3 rounded">
                      <p className="text-2xl font-bold text-red-900 font-mono">£0.00</p>
                      <p className="text-sm text-red-700 mt-1">No SSP payable</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* April 2026 Rules */}
            <div className="card border-accent-200">
              <div className="card-header bg-accent-50">
                <h3 className="font-semibold text-accent-900">April 2026 Rules</h3>
              </div>
              <div className="card-body space-y-4">
                {/* Step 2: Eligibility Check */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Step 2: Eligibility Checks</h4>
                  <div className="space-y-2 text-sm">
                    {/* No LEL */}
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-green-700">
                          <strong>Lower Earnings Limit:</strong> Abolished ✓
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          All employees qualify regardless of earnings
                        </p>
                      </div>
                    </div>

                    {/* No PIW requirement */}
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-green-700">
                          <strong>Period of Incapacity (PIW):</strong> Removed ✓
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Any absence length qualifies (even 1 day)
                        </p>
                      </div>
                    </div>

                    {/* Always Eligible */}
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                      <p className="text-green-900 font-medium">✓ Eligible for SSP</p>
                      <p className="text-xs text-green-700 mt-1">Universal eligibility from April 2026</p>
                    </div>
                  </div>
                </div>

                {/* Step 3: SSP Rate Determination */}
                <div className="pt-4 border-t border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-2">Step 3: SSP Rate Determination</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-slate-700">
                      Standard weekly rate: <span className="font-mono font-semibold">{formatCurrency(SSP_WEEKLY_RATE_2026)}</span>
                    </p>
                    <p className="text-slate-700">
                      80% threshold: <span className="font-mono font-semibold">{formatCurrency(EIGHTY_PERCENT_THRESHOLD_2026)}</span>
                    </p>

                    {aweResult.awe < EIGHTY_PERCENT_THRESHOLD_2026 ? (
                      <div className="bg-amber-50 border border-amber-200 p-2 rounded">
                        <p className="text-amber-900 font-medium">Low Earner Rate Applies</p>
                        <p className="text-xs text-amber-700 mt-1">
                          AWE {formatCurrency(aweResult.awe)} &lt; {formatCurrency(EIGHTY_PERCENT_THRESHOLD_2026)}
                        </p>
                        <p className="text-amber-900 mt-2">
                          Rate = 80% of AWE = {formatCurrency(aweResult.awe * 0.8)}
                        </p>
                        <p className="text-xs text-amber-700">
                          Capped at standard rate {formatCurrency(SSP_WEEKLY_RATE_2026)}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 p-2 rounded">
                        <p className="text-green-900 font-medium">Standard Rate Applies</p>
                        <p className="text-xs text-green-700 mt-1">
                          AWE {formatCurrency(aweResult.awe)} ≥ {formatCurrency(EIGHTY_PERCENT_THRESHOLD_2026)}
                        </p>
                      </div>
                    )}

                    <p className="text-slate-700 mt-2">
                      Weekly SSP rate: <span className="font-mono font-semibold">{formatCurrency(newSSP.weeklyRate)}</span>
                    </p>
                    <p className="text-slate-700">
                      Qualifying days per week: <span className="font-semibold">{employee.qualifyingDays}</span>
                    </p>
                    <div className="bg-accent-50 p-2 rounded mt-2">
                      <p className="text-accent-900">
                        Daily rate = {formatCurrency(newSSP.weeklyRate)} ÷ {employee.qualifyingDays} = <span className="font-mono font-bold">{formatCurrency(newSSP.dailyRate)}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 4: No Waiting Days */}
                <div className="pt-4 border-t border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-2">Step 4: Apply Waiting Days</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-slate-700">
                      Rostered qualifying days during absence: <span className="font-semibold">{newSSP.rosteredDays || absence.rosteredDays}</span>
                    </p>
                    <div className="bg-green-50 border border-green-200 p-2 rounded">
                      <p className="text-green-900 font-medium">No Waiting Days ✓</p>
                      <p className="text-xs text-green-700 mt-1">
                        SSP payable from day 1 of sickness
                      </p>
                    </div>
                    <div className="bg-accent-50 p-2 rounded mt-2">
                      <p className="text-accent-900">
                        SSP payable days = <span className="font-semibold">{newSSP.sspDaysPaid}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 5: Total SSP */}
                <div className="pt-4 border-t border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-2">Step 5: Total SSP</h4>
                  <div className="bg-accent-100 p-3 rounded">
                    <p className="text-sm text-accent-700 mb-1">
                      {newSSP.sspDaysPaid} days × {formatCurrency(newSSP.dailyRate)}/day
                    </p>
                    <p className="text-2xl font-bold text-accent-900 font-mono">
                      {formatCurrency(newSSP.totalSSP)}
                    </p>
                  </div>
                </div>

                {/* 28-Week Maximum Tracking */}
                {(newSSP.weeksUsed > 0 || newSSP.wasCapped) && (
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="font-medium text-slate-900 mb-2">28-Week Maximum</h4>
                    <div className={`p-3 rounded border ${
                      newSSP.maxReached
                        ? 'bg-red-50 border-red-300'
                        : newSSP.weeksRemaining < 4
                        ? 'bg-amber-50 border-amber-300'
                        : 'bg-blue-50 border-blue-300'
                    }`}>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-700">Previous SSP weeks:</span>
                          <span className="font-mono font-semibold">{(absence.previousSSPWeeks || 0).toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-700">This absence adds:</span>
                          <span className="font-mono font-semibold">+{(newSSP.weeksUsed - (absence.previousSSPWeeks || 0)).toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-300">
                          <span className="font-medium text-slate-900">Total weeks used:</span>
                          <span className="font-mono font-bold text-lg">{newSSP.weeksUsed.toFixed(1)} / 28</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-700">Weeks remaining:</span>
                          <span className={`font-mono font-semibold ${
                            newSSP.weeksRemaining < 4 ? 'text-amber-700' : 'text-green-700'
                          }`}>
                            {newSSP.weeksRemaining.toFixed(1)}
                          </span>
                        </div>
                        {newSSP.wasCapped && (
                          <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded">
                            <p className="text-amber-900 font-medium flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              Payment Capped
                            </p>
                            <p className="text-xs text-amber-800 mt-1">
                              SSP limited to remaining entitlement
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Summary Comparison */}
          <div className="card border-2 border-slate-300">
            <div className="card-header bg-slate-100">
              <h3 className="font-semibold text-slate-900">Summary Comparison</h3>
            </div>
            <div className="card-body">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="text-left">Calculation Step</th>
                      <th className="text-center">Current Rules</th>
                      <th className="text-center">April 2026</th>
                      <th className="text-center">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="font-medium">Eligibility Check</td>
                      <td className="text-center">
                        {currentSSP.eligible ? (
                          <span className="text-green-600 font-medium">✓ Eligible</span>
                        ) : (
                          <span className="text-red-600 font-medium">✗ Not Eligible</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="text-green-600 font-medium">✓ Eligible</span>
                      </td>
                      <td className="text-center">
                        {!currentSSP.eligible && (
                          <span className="text-green-600 font-medium">↑ Gains eligibility</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="font-medium">Weekly SSP Rate</td>
                      <td className="text-center font-mono">{currentSSP.eligible ? formatCurrency(currentSSP.weeklyRate || SSP_WEEKLY_RATE) : '—'}</td>
                      <td className="text-center font-mono">{formatCurrency(newSSP.weeklyRate)}</td>
                      <td className="text-center font-mono text-accent-600">
                        {currentSSP.eligible ? formatCurrency((newSSP.weeklyRate || SSP_WEEKLY_RATE_2026) - (currentSSP.weeklyRate || SSP_WEEKLY_RATE)) : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="font-medium">Daily SSP Rate</td>
                      <td className="text-center font-mono">{currentSSP.eligible ? formatCurrency(currentSSP.dailyRate) : '—'}</td>
                      <td className="text-center font-mono">{formatCurrency(newSSP.dailyRate)}</td>
                      <td className="text-center font-mono text-accent-600">
                        {currentSSP.eligible ? formatCurrency(newSSP.dailyRate - currentSSP.dailyRate) : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="font-medium">Waiting Days</td>
                      <td className="text-center">{currentSSP.eligible ? currentSSP.waitingDays : '—'}</td>
                      <td className="text-center text-green-600 font-medium">0</td>
                      <td className="text-center">
                        {currentSSP.eligible && currentSSP.waitingDays > 0 && (
                          <span className="text-green-600 font-medium">-{currentSSP.waitingDays} days</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="font-medium">SSP Days Paid</td>
                      <td className="text-center font-semibold">{currentSSP.sspDaysPaid || 0}</td>
                      <td className="text-center font-semibold">{newSSP.sspDaysPaid}</td>
                      <td className="text-center font-semibold text-accent-600">
                        +{newSSP.sspDaysPaid - (currentSSP.sspDaysPaid || 0)}
                      </td>
                    </tr>
                    <tr className="bg-slate-50 font-bold">
                      <td className="font-bold">Total SSP Payable</td>
                      <td className="text-center font-mono text-primary-700">{formatCurrency(currentSSP.totalSSP)}</td>
                      <td className="text-center font-mono text-accent-700">{formatCurrency(newSSP.totalSSP)}</td>
                      <td className="text-center font-mono text-red-700">
                        +{formatCurrency(newSSP.totalSSP - currentSSP.totalSSP)}
                        {currentSSP.totalSSP > 0 && (
                          <span className="text-xs ml-1">
                            ({(((newSSP.totalSSP - currentSSP.totalSSP) / currentSSP.totalSSP) * 100).toFixed(0)}%)
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Key Insights */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">Key Changes from April 2026:</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                      {!currentSSP.eligible && (
                        <li>Employee gains SSP eligibility (previously excluded by LEL or PIW requirements)</li>
                      )}
                      {currentSSP.waitingDays > 0 && (
                        <li>No waiting days - SSP paid from day 1 (currently {currentSSP.waitingDays} waiting days)</li>
                      )}
                      {absence.totalSickDays < 4 && (
                        <li>Short absences (1-3 days) now qualify for SSP (currently require 4+ days)</li>
                      )}
                      {newSSP.isReducedRate && (
                        <li>Lower earners receive 80% of AWE (capped at standard rate)</li>
                      )}
                      <li>Weekly SSP rate increases from {formatCurrency(SSP_WEEKLY_RATE)} to {formatCurrency(SSP_WEEKLY_RATE_2026)}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
